import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Command } from 'cmdk'
import { useTranslation } from 'react-i18next'
import { Check, CornerDownRight, Plus, Search } from 'lucide-react'
import { LifeSpaceColorIcon } from '@/src/renderer/components/icons/LifeSpaceColorIcon'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import { ShortcutBadge } from '@/src/renderer/components/ui/Button/ShortcutBadge'
import type { LifeSpace, Project } from '@/src/renderer/lib/db/models'
import { highlightParts, searchWords } from '@/src/renderer/lib/search/matchSearch'
import { useMoveDestinations } from '../../hooks/useMoveDestinations'
import { moveCounts, moveRows, moveSpaceOrder, offersNewProject, type MoveCurrent, type MoveRow } from '../../utils/moveTargets'
import type { MoveTarget } from '../../utils/moveTasks'
import './MovePicker.scss'

/** Der Wert der Zeile „Neues Projekt „…““ — Projekte und Abschnitte heißen `p:…` / `s:…`. */
const NEW_PROJECT = 'new-project'

export interface MovePickerProps {
  /** „„Rasen mähen“ verschieben“ — oder „3 Aufgaben verschieben“. */
  title: string
  /** Der Lebensbereich der angefassten Aufgabe: steht in der **Lebensbereich-Wahl** links. */
  startSpace: LifeSpace
  /**
   * Gewählt beim Öffnen — der zuletzt gewählte Bereich (#279). Fehlt er, ist es `startSpace`. Das Panel selbst
   * merkt sich nichts; das tun die Hüllen (`lastMoveSpace.ts`).
   */
  initialSpace?: LifeSpace | null
  /** Eine ausdrückliche Wahl (←/→, Klick) bei leerem Suchfeld — nicht das Öffnen, nicht das Tippen, nicht in der Suche. */
  onSpaceChange?: (space: LifeSpace) => void
  /** Der heutige Platz (Haken, gesperrt). `null` bei mehreren Gewählten. */
  current: MoveCurrent
  /** `place` nennt das Ziel für die Meldung: „Garten“ oder „Garten / Beete“; `project` trägt das Icon dazu. */
  onSelect: (target: MoveTarget, place: string, project: Project) => void
  /** „Neues Projekt „…“ in <Lebensbereich>“ (entschieden 05.10.2026). */
  onCreateProject: (project: { name: string; lifeSpace: LifeSpace }) => void
}

/**
 * **Verschieben** (#263, CONTEXT.md) — das Panel an der Zeile, gebaut wie die **Suche**: oben das
 * Feld, darunter die **Lebensbereich-Wahl** (←/→), darunter die Projekte mit ihren Abschnitten. Beim
 * Tippen stehen die Treffer aller Lebensbereiche nach Lebensbereich gruppiert da. Nur der Inhalt —
 * die Hülle ist `MoveShortcutBox` (TaskDropdown).
 *
 * @example
 * <MovePicker title="„Rasen mähen“ verschieben" startSpace="home"
 *   initialSpace={readLastMoveSpace()} onSpaceChange={rememberMoveSpace}
 *   current={{ projectId: 'garten', sectionId: 'rasen' }}
 *   onSelect={(target, place) => move(id, target, place)}
 *   onCreateProject={project => moveToNewProject(id, project)} />
 */
export const MovePicker: React.FC<MovePickerProps> = ({ title, startSpace, initialSpace, onSpaceChange, current, onSelect, onCreateProject }) => {
  const { t } = useTranslation()
  const targets = useMoveDestinations()
  const spaces = useMemo(() => moveSpaceOrder(startSpace), [startSpace])
  const [space, setSpace] = useState<LifeSpace>(() => initialSpace ?? startSpace)
  const [query, setQuery] = useState('')
  // Leer heißt: die erste wählbare Zeile — so steht die Markierung nach jedem Tippen wieder oben.
  const [selected, setSelected] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const words = useMemo(() => searchWords(query), [query])
  const searching = words.length > 0
  const groups = useMemo(() => {
    if (!targets) return []
    if (!searching) return [{ lifeSpace: space, rows: moveRows(targets, space, '', current) }]
    return spaces.map(lifeSpace => ({ lifeSpace, rows: moveRows(targets, lifeSpace, query, current) })).filter(group => group.rows.length > 0)
  }, [targets, searching, space, spaces, query, current])
  const rows = useMemo(() => groups.flatMap(group => group.rows), [groups])
  const counts = useMemo(() => (targets ? moveCounts(targets, query) : null), [targets, query])
  const newProjectName = query.trim().replace(/\s+/g, ' ')
  const offersNew = (lifeSpace: LifeSpace) => !!targets && offersNewProject(targets, lifeSpace, query)

  const firstRowIn = (lifeSpace: LifeSpace | null) => rows.find(row => !row.here && (lifeSpace == null || row.lifeSpace === lifeSpace))
  const isValid = (value: string) => (value === NEW_PROJECT ? offersNew(space) : rows.some(row => row.key === value && !row.here))
  const value = isValid(selected) ? selected : (firstRowIn(space) ?? firstRowIn(null))?.key ?? (offersNew(space) ? NEW_PROJECT : '')

  /*
    Fokus ins Feld — auch wenn das Panel gerade erst sichtbar wird (TaskDropdown zeigt es erst nach
    dem Positionieren) oder das Aufgaben-Menü beim Schließen den Fokus noch zurücklegt.
  */
  useEffect(() => {
    let frame = 0
    let tries = 0
    const grab = () => {
      const input = inputRef.current
      if (!input) return
      if (document.activeElement !== input) input.focus({ preventScroll: true })
      if (tries++ < 10) frame = requestAnimationFrame(grab)
    }
    frame = requestAnimationFrame(grab)
    return () => cancelAnimationFrame(frame)
  }, [])

  /*
    Die Wahl ändern nur ←/→ und ein Klick auf einen Lebensbereich — nicht das Tippen, nicht ↓, nicht der
    Zeiger. Sie sagt, wo „Neues Projekt“ entsteht; folgte sie der Markierung, spränge die Anlege-Zeile
    beim Weg nach unten in einen anderen Lebensbereich (Diff-Audit 05.10.2026).
  */
  const changeQuery = (next: string) => {
    setQuery(next)
    setSelected('')
  }

  const chooseSpace = (lifeSpace: LifeSpace) => {
    setSpace(lifeSpace)
    // Gemerkt wird nur ohne Suche — dort ist ←/→ der Weg zur Gruppe, keine Wahl (Sven, 06.10.2026).
    if (!searching) onSpaceChange?.(lifeSpace)
    // Beim Tippen: zur Gruppe des Lebensbereichs — oder, hat er keine Treffer, auf „Neues Projekt“.
    setSelected(searching ? (firstRowIn(lifeSpace)?.key ?? (offersNew(lifeSpace) ? NEW_PROJECT : '')) : '')
  }

  // cmdk rollt nur mit, wenn es selbst markiert; ←/→ markiert von außen (Diff-Audit 05.10.2026).
  useEffect(() => {
    if (!value) return
    rootRef.current?.querySelector(`[cmdk-item][data-value="${value}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [value])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const plain = !(event.shiftKey || event.altKey || event.metaKey || event.ctrlKey || event.nativeEvent.isComposing)
    if (!plain || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return
    // ←/→ gehören der Lebensbereich-Wahl, nicht dem Caret — wie in der Suche. Den Caret schieben ⌥/⌘.
    event.preventDefault()
    const step = event.key === 'ArrowRight' ? 1 : spaces.length - 1
    chooseSpace(spaces[(spaces.indexOf(space) + step) % spaces.length]!)
  }

  const renderRow = (row: MoveRow) => (
    <Command.Item
      key={row.key}
      value={row.key}
      disabled={row.here}
      className={`move-picker__row move-picker__row--${row.kind} move-picker__row--depth-${Math.min(row.depth, 4)}`}
      data-space={row.lifeSpace}
      onSelect={() => onSelect(row.target, row.place, row.project)}
    >
      <span className={`move-picker__icon${row.kind === 'section' ? ' move-picker__icon--section' : ''}`} aria-hidden="true">
        {row.kind === 'section'
          ? <CornerDownRight size={14} />
          : <ProjectIcon icon={row.project.icon} iconColor={row.project.iconColor} size={16} />}
      </span>
      <span className="move-picker__name"><Highlight text={row.section?.title ?? row.project.name} words={words} /></span>
      {row.here && <Check className="move-picker__check" size={14} strokeWidth={2} aria-hidden="true" />}
      <span className="move-picker__hint" aria-hidden="true"><ShortcutBadge variant="outline" shortcut={{ kind: 'return' }} /></span>
    </Command.Item>
  )

  return (
    <Command
      ref={rootRef}
      className="move-picker"
      data-space={space}
      label={title}
      shouldFilter={false}
      vimBindings={false}
      value={value}
      onValueChange={setSelected}
      onKeyDown={handleKeyDown}
    >
      <div className="move-picker__title">{title}</div>
      <div className="move-picker__field">
        <Search size={16} className="move-picker__field-icon" aria-hidden="true" />
        <Command.Input
          ref={inputRef}
          className="move-picker__input"
          value={query}
          onValueChange={changeQuery}
          placeholder={t('taskItem.move.searchPlaceholder')}
          spellCheck={false}
        />
      </div>

      <div className="move-picker__spaces" role="group" aria-label={t('taskItem.move.spacesLabel')}>
        {spaces.map(lifeSpace => (
          <button
            key={lifeSpace}
            type="button"
            className={`move-picker__space${lifeSpace === space ? ' move-picker__space--selected' : ''}`}
            data-space={lifeSpace}
            aria-pressed={lifeSpace === space}
            // Der Fokus bleibt im Feld; die Tastatur erreicht die Wahl über ←/→.
            tabIndex={-1}
            onMouseDown={event => event.preventDefault()}
            onClick={() => chooseSpace(lifeSpace)}
          >
            <LifeSpaceColorIcon lifeSpace={lifeSpace} size={12} />
            {t(`lifeSpaces.${lifeSpace}`)}
            {counts && <span className="move-picker__space-count">{counts[lifeSpace]}</span>}
          </button>
        ))}
      </div>

      <Command.List className="move-picker__list">
        {targets && !searching && rows.length === 0 && <div className="move-picker__empty">{t('taskItem.move.noProjects')}</div>}
        {targets && searching && rows.length === 0 && <div className="move-picker__empty">{t('taskItem.move.nothingFound', { query: newProjectName })}</div>}
        {searching
          ? groups.map(group => (
            <Command.Group
              key={group.lifeSpace}
              className="move-picker__group"
              data-space={group.lifeSpace}
              heading={<><LifeSpaceColorIcon lifeSpace={group.lifeSpace} size={12} />{t(`lifeSpaces.${group.lifeSpace}`)}</>}
            >
              {group.rows.map(renderRow)}
            </Command.Group>
          ))
          : rows.map(renderRow)}
        {searching && offersNew(space) && (
          <Command.Item
            value={NEW_PROJECT}
            className="move-picker__row move-picker__row--new"
            data-space={space}
            onSelect={() => onCreateProject({ name: newProjectName, lifeSpace: space })}
          >
            <span className="move-picker__icon" aria-hidden="true"><Plus size={16} /></span>
            <span className="move-picker__name">{t('taskItem.move.newProject', { name: newProjectName, space: t(`lifeSpaces.${space}`) })}</span>
            <span className="move-picker__hint" aria-hidden="true"><ShortcutBadge variant="outline" shortcut={{ kind: 'return' }} /></span>
          </Command.Item>
        )}
      </Command.List>

      <div className="move-picker__footer" aria-hidden="true">
        <span className="move-picker__footer-item">
          <ShortcutBadge variant="subtle" shortcut={[{ kind: 'character', text: '↑' }, { kind: 'character', text: '↓' }]} />
          {t('search.footer.navigate')}
        </span>
        <span className="move-picker__footer-item">
          <ShortcutBadge variant="subtle" shortcut={{ kind: 'return' }} />
          {t('taskItem.move.footerMove')}
        </span>
        <span className="move-picker__footer-item">
          <ShortcutBadge variant="subtle" shortcut={[{ kind: 'character', text: '←' }, { kind: 'character', text: '→' }]} />
          {t('taskItem.move.footerSpace')}
        </span>
      </div>
    </Command>
  )
}

/** Getroffene Stücke markiert — wie in der Suche. */
function Highlight({ text, words }: { text: string; words: readonly string[] }) {
  return (
    <>
      {highlightParts(text, words).map((part, i) => (part.hit
        ? <mark key={i} className="move-picker__mark">{part.text}</mark>
        : <React.Fragment key={i}>{part.text}</React.Fragment>))}
    </>
  )
}
